import React, { useState, useEffect, Suspense, lazy, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Container,
  Paper,
  Stack,
  IconButton,
  Tooltip,
  Avatar,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Modal
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import LoadingScreen from '../components/common/LoadingScreen';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import PostAddIcon from '@mui/icons-material/PostAdd';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ImageIcon from '@mui/icons-material/Image';
import TopicIcon from '@mui/icons-material/Topic';
import PeopleIcon from '@mui/icons-material/People';
import LaunchIcon from '@mui/icons-material/Launch';
import ArticleIcon from '@mui/icons-material/Article';

// Dynamic import for GaugeComponent
const GaugeComponent = lazy(() => import('react-gauge-component'));

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '1200px',
  bgcolor: 'transparent',
  boxShadow: 24,
  p: 0,
  outline: 'none',
  height: '95vh',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px',
};

const Dashboard = () => {
  const { user, updateUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkedinProfile, setLinkedinProfile] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [metrics, setMetrics] = useState({
    aboutScore: null,
    drafts: [],
    scheduledPosts: []
  });
  const [suggestedTopics, setSuggestedTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch user and LinkedIn profile in parallel
        let userData, linkedInProfileRes;
        try {
          const [userProfileRes, linkedInProfileResponse] = await Promise.all([
            axios.get(
              `${process.env.REACT_APP_API_URL}/api/auth/profile`,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`
                }
              }
            ),
            axios.get(
              `${process.env.REACT_APP_API_URL}/api/linkedin/profile-data`,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`
                }
              }
            )
          ]);
          
          userData = userProfileRes.data.user;
          linkedInProfileRes = linkedInProfileResponse;
          setUserProfile(userData);
          setLinkedinProfile(linkedInProfileRes.data);
          console.log('User profile data:', userData);
          console.log('LinkedIn profile data from API:', linkedInProfileRes.data);
        } catch (error) {
          console.error('Error fetching user or LinkedIn profile:', error);
          userData = {};
          linkedInProfileRes = { data: {} };
          setError('Failed to load profile data');
        }

        // Extract username from the user profile
        const extractUsername = (url) => {
          if (!url) return '';
          if (!url.includes('linkedin.com')) return url;
          const matches = url.match(/linkedin\.com\/in\/([^\/\?]+)/);
          return matches ? matches[1] : url;
        };

        const username = extractUsername(userData?.linkedinProfile);
        console.log('LinkedIn profile URL from user data:', userData?.linkedinProfile);
        console.log('Extracted username:', username);

        // Initialize all responses with safe default values
        let aboutRes = { data: { success: false, analysis: { analysis_score: null } } };
        let draftsRes = { data: [] };

        try {
          aboutRes = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/linkedin/analysis/about`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
        } catch (error) {
          console.error('Error fetching about analysis:', error);
        }
        
        try {
          draftsRes = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/posts`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
        } catch (error) {
          console.error('Error fetching drafts:', error);
        }

        // Process draft posts
        let draftResults = [];
        if (draftsRes.data && Array.isArray(draftsRes.data)) {
          // Filter for draft posts (not scheduled or posted)
          const draftPosts = draftsRes.data.filter(post => 
            !post.scheduled_date && !post.posted_to_linkedin
          ).slice(0, 4); // Get only the 4 most recent drafts

          console.log('Draft posts:', draftPosts);
          // Format draft posts
          draftResults = draftPosts.map(post => ({
            id: post.id,
            content: post.content,
            topic: post.topic || 'Untitled Post',
            type: post.type,
            image: post.image,
            attachment: post.attachment,
            created_at: post.created_at,
            scheduled_date: post.scheduled_date,
            posted_to_linkedin: post.posted_to_linkedin,
            linkedin_post_url: post.linkedin_post_url,
            stats: {
              totalReactions: 0,
              comments: 0,
              reposts: 0
            }
          }));
        }

        // Set the metrics with combined updates in a single call to prevent multiple re-renders
        setMetrics({
          aboutScore: aboutRes.data.success ? aboutRes.data.analysis.analysis_score : null,
          drafts: draftResults || [],
          scheduledPosts: []
        });
        
        // Generate topics separately (API call) - this doesn't block showing scores and posts
        setTopicsLoading(true);
        try {
          // Use the data from onboarding that should already be saved in the user profile
          const industry = userData?.industry || '';
          const professionalRole = userData?.professionalRole || '';
          const targetAudience = userData?.targetAudience || '';
          
          const payload = {
            settings: {
              firstStep: ['Educational Content'],
              secondStep: ['Building a personal brand'],
              thirdStep: ['Professional'],
              fourthStep: [industry],
              fifthStep: [professionalRole],
              sixthStep: professionalRole,
              seventhStep: targetAudience
            }
          };

          console.log('Topic generation payload:', payload);

          const topicsRes = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/ai/topics`,
            payload,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }
          );

          if (Array.isArray(topicsRes.data) && topicsRes.data.length > 0) {
            setSuggestedTopics(topicsRes.data.slice(0, 5));
            console.log('Topics generated successfully:', topicsRes.data.slice(0, 5));
          } else {
            console.warn('Topics API returned empty or invalid response:', topicsRes.data);
            setSuggestedTopics([]);
          }
        } catch (err) {
          console.error('Error generating topics:', err);
          setSuggestedTopics([]);
        } finally {
          setTopicsLoading(false);
        }

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const ScoreMeter = ({ score, label, icon, onClick }) => (
    <Card 
      onClick={onClick}
      sx={{ 
        height: '100%',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        background: '#ffffff',
        border: '1px solid rgba(72, 144, 254, 0.1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 4px 12px rgba(72, 144, 254, 0.08)',
          borderColor: 'rgba(72, 144, 254, 0.2)'
        }
      }}
    >
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              bgcolor: 'rgba(72, 144, 254, 0.08)',
              color: 'var(--primary-color)',
              mr: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {icon}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2D3748' }}>
              {label}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" sx={{ color: '#718096', fontWeight: 500 }}>
              Score: {score || 0}/70
            </Typography>
            <IconButton
              size="small"
              sx={{
                color: '#718096',
                '&:hover': {
                  backgroundColor: 'rgba(72, 144, 254, 0.08)',
                  color: 'var(--primary-color)'
                }
              }}
            >
              <LaunchIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          position: 'relative'
        }}>
          <Suspense fallback={<CircularProgress size={80} thickness={2} sx={{ color: 'var(--primary-color)' }} />}>
            <GaugeComponent
              type="semicircle"
              arc={{
                width: 0.2,
                padding: 0.005,
                cornerRadius: 1,
                subArcs: [
                  {
                    limit: 30,
                    color: '#FF6B6B',
                    showTick: true
                  },
                  {
                    limit: 50,
                    color: '#FF9F89',
                    showTick: true
                  },
                  {
                    limit: 70,
                    color: '#4299E1',
                    showTick: true
                  },
                  {
                    limit: 90,
                    color: '#63B3ED',
                    showTick: true
                  },
                  {
                    color: '#48BB78',
                    showTick: true
                  }
                ]
              }}
              pointer={{
                type: "arrow",
                color: '#2D3748',
                length: 0.8,
                width: 15,
                elastic: false,
              }}
              labels={{
                valueLabel: { 
                  formatTextValue: value => Math.round(value) + '%',
                  style: { 
                    textShadow: 'none', 
                    fill: '#2D3748', 
                    fontSize: 32,
                    fontWeight: 600,
                    dominantBaseline: 'central',
                    textAnchor: 'middle',
                    transform: 'translateY(0)',
                    y: '40%'
                  }
                },
                tickLabels: {
                  type: 'outer',
                  defaultTickValueConfig: {
                    formatTextValue: value => value,
                    style: { textShadow: 'none', fill: '#718096', fontSize: 12 }
                  }
                }
              }}
              value={score ? Math.round((score / 70) * 100) : 0}
              minValue={0}
              maxValue={100}
            />
          </Suspense>
        </Box>
      </CardContent>
    </Card>
  );

  const QuickActionButton = ({ icon, label, onClick, primary }) => (
    <Button
      fullWidth
      variant={primary ? "contained" : "outlined"}
      startIcon={icon}
      onClick={onClick}
      sx={{
        py: 1.5,
        px: 2,
        bgcolor: primary ? 'var(--primary-color)' : 'transparent',
        color: primary ? 'white' : 'var(--primary-color)',
        border: primary ? 'none' : '1px solid rgba(72, 144, 254, 0.5)',
        fontWeight: 600,
        fontSize: '0.875rem',
        textTransform: 'none',
        borderRadius: 2,
        boxShadow: primary ? '0 2px 8px rgba(72, 144, 254, 0.15)' : 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: primary ? 'var(--primary-dark)' : 'rgba(72, 144, 254, 0.08)',
          border: primary ? 'none' : '1px solid rgba(72, 144, 254, 0.8)',
          boxShadow: primary ? '0 4px 12px rgba(72, 144, 254, 0.2)' : 'none',
          transform: 'translateY(-2px)'
        }
      }}
    >
      {label}
    </Button>
  );

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dashboard | Instation</title>
        <meta name="description" content="View your LinkedIn profile analytics, content performance, and manage your social media presence." />
      </Helmet>
      
      <Container 
        maxWidth="xl" 
        sx={{ 
          height: '100%',
          py: 3,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{ 
            flex: 1,
            overflow: 'auto',
            height: '100%'
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 4,
                  borderRadius: 3,
                  border: '1px solid rgba(72, 144, 254, 0.1)',
                  background: '#ffffff'
                }}
              >
                {/* LinkedIn Profile Section */}
                <Box sx={{ 
                  mb: 5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3
                }}>
                  {/* Profile Info Card */}
                  <Card sx={{ 
                    bgcolor: 'rgba(72, 144, 254, 0.03)',
                    border: '1px solid rgba(72, 144, 254, 0.08)',
                    boxShadow: 'none',
                    borderRadius: 3
                  }}>
                    <CardContent sx={{ 
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 3,
                      p: 3,
                      '&:last-child': { pb: 3 }
                    }}>
                      <Avatar 
                        src={linkedinProfile?.profile_picture}
                        alt={linkedinProfile?.name}
                        sx={{ 
                          width: 80, 
                          height: 80,
                          border: '2px solid rgba(72, 144, 254, 0.2)'
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" sx={{ 
                          fontWeight: 700,
                          color: '#2D3748',
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2
                        }}>
                          Welcome back, {linkedinProfile?.name || user?.name || 'User'}
                          <Box 
                            component="span" 
                            sx={{ 
                              display: 'inline-block',
                              animation: 'waving 2.5s infinite',
                              transformOrigin: '70% 70%',
                              '@keyframes waving': {
                                '0%': { transform: 'rotate(0deg)' },
                                '10%': { transform: 'rotate(14deg)' },
                                '20%': { transform: 'rotate(-8deg)' },
                                '30%': { transform: 'rotate(14deg)' },
                                '40%': { transform: 'rotate(-4deg)' },
                                '50%': { transform: 'rotate(10deg)' },
                                '60%': { transform: 'rotate(0deg)' },
                                '100%': { transform: 'rotate(0deg)' }
                              }
                            }}
                          >
                            👋
                          </Box>
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          color: '#718096',
                          mb: 1
                        }}>
                          {linkedinProfile?.headline || 'LinkedIn Member'}
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography variant="body2" sx={{ color: '#718096' }}>
                            {linkedinProfile?.location}
                          </Typography>
                          <Box 
                            component="span" 
                            sx={{ 
                              width: 4,
                              height: 4,
                              borderRadius: '50%',
                              bgcolor: '#CBD5E0'
                            }} 
                          />
                          <Typography variant="body2" sx={{ color: '#718096' }}>
                            {linkedinProfile?.connection_count || '0'} connections
                          </Typography>
                        </Stack>
                      </Box>
                      <Tooltip title="View LinkedIn Profile">
                        <IconButton 
                          sx={{ 
                            bgcolor: 'rgba(10, 102, 194, 0.1)',
                            '&:hover': { 
                              bgcolor: 'rgba(10, 102, 194, 0.2)',
                              transform: 'translateY(-2px)',
                              transition: 'all 0.2s ease'
                            }
                          }}
                        >
                          <LinkedInIcon sx={{ color: '#0A66C2' }} />
                        </IconButton>
                      </Tooltip>
                    </CardContent>
                  </Card>
                </Box>

                {/* Profile Analysis Scores */}
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#2D3748' }}>
                  Profile Analysis
                </Typography>
                <Grid container spacing={3} sx={{ mb: 5 }}>
                  <Grid item xs={12} md={6}>
                    <ScoreMeter
                      score={metrics.aboutScore}
                      label="About Section"
                      icon={<DescriptionIcon sx={{ fontSize: 24 }} />}
                      onClick={() => navigate('/dashboard/about-section')}
                    />
                  </Grid>
                </Grid>

                {/* Customized Topics */}
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#2D3748' }}>
                  Start with Customized Topics
                </Typography>
                <Card sx={{ 
                  mb: 5,
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                  border: '1px solid rgba(0, 0, 0, 0.05)'
                }}>
                  <CardContent>
                    {topicsLoading ? (
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        minHeight: 200
                      }}>
                        <CircularProgress size={40} sx={{ color: 'var(--primary-color)' }} />
                      </Box>
                    ) : suggestedTopics.length > 0 ? (
                      <Grid container spacing={2}>
                        {suggestedTopics.map((item, index) => (
                          <Grid item xs={12} key={index}>
                            <Button
                              fullWidth
                              variant="outlined"
                              onClick={() => navigate('/dashboard/generate-post', { 
                                state: { 
                                  topic: item.topic,
                                  autoFill: true
                                } 
                              })}
                              sx={{ 
                                p: 2,
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                textTransform: 'none',
                                borderRadius: 2,
                                color: '#2D3748',
                                borderColor: 'rgba(0, 0, 0, 0.1)',
                                backgroundColor: '#fff',
                                '&:hover': {
                                  borderColor: '#4890fe',
                                  backgroundColor: 'rgba(72, 144, 254, 0.04)',
                                }
                              }}
                            >
                              {item.topic}
                            </Button>
                          </Grid>
                        ))}
                        <Grid item xs={12}>
                          <Button
                            fullWidth
                            variant="text"
                            onClick={() => navigate('/dashboard/topic-generator')}
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                              mt: 1,
                              color: 'var(--primary-color)',
                              textTransform: 'none',
                              fontWeight: 500,
                              '&:hover': {
                                backgroundColor: 'rgba(72, 144, 254, 0.08)'
                              }
                            }}
                          >
                            Generate More Topics
                          </Button>
                        </Grid>
                      </Grid>
                    ) : (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 200,
                        textAlign: 'center',
                        color: '#718096'
                      }}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          No topics generated yet. Click below to generate customized topics.
                        </Typography>
                        <Button
                          variant="outlined"
                          onClick={() => navigate('/dashboard/topic-generator')}
                          sx={{
                            color: 'var(--primary-color)',
                            borderColor: 'var(--primary-color)',
                            '&:hover': {
                              borderColor: 'var(--primary-color)',
                              backgroundColor: 'rgba(72, 144, 254, 0.08)'
                            }
                          }}
                        >
                          Generate Topics
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <Box sx={{ mb: 5 }}>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#2D3748' }}>
                    Quick Actions
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <QuickActionButton
                        icon={<PostAddIcon />}
                        label="Create New Post"
                        onClick={() => navigate('/dashboard/generate-post')}
                        primary
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <QuickActionButton
                        icon={<ImageIcon />}
                        label="Generate Image"
                        onClick={() => navigate('/dashboard/generate-image')}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <QuickActionButton
                        icon={<TopicIcon />}
                        label="Topic Generator"
                        onClick={() => navigate('/dashboard/topic-generator')}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <QuickActionButton
                        icon={<PeopleIcon />}
                        label="View Influencers"
                        onClick={() => navigate('/dashboard/influencers')}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Content Overview */}
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#2D3748' }}>
                  Content Overview
                </Typography>
                <Grid container spacing={3}>
                  {/* Recent Drafts */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ 
                      height: '100%',
                      background: '#ffffff',
                      border: '1px solid rgba(72, 144, 254, 0.1)',
                      boxShadow: 'none',
                      borderRadius: 2
                    }}>
                      <CardContent>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 3
                        }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2D3748' }}>
                            Recent Drafts
                          </Typography>
                          <Button
                            size="small"
                            endIcon={<ArrowForwardIcon />}
                            onClick={() => navigate('/dashboard/posts')}
                            sx={{
                              color: 'var(--primary-color)',
                              '&:hover': { bgcolor: 'rgba(72, 144, 254, 0.08)' }
                            }}
                          >
                            View All
                          </Button>
                        </Box>
                        {loading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={24} sx={{ color: 'var(--primary-color)' }} />
                          </Box>
                        ) : metrics.drafts && metrics.drafts.length > 0 ? (
                          <TableContainer>
                            <Table>
                              <TableBody>
                                {metrics.drafts.map((post, index) => (
                                  <TableRow 
                                    key={index} 
                                    hover 
                                    sx={{ 
                                      cursor: 'pointer',
                                      '&:hover': { bgcolor: 'action.hover' }
                                    }}
                                    onClick={() => navigate('/dashboard/posts')}
                                  >
                                    <TableCell sx={{ width: 80, pl: 2 }}>
                                      {post.image ? (
                                        <Box
                                          component="img"
                                          src={post.image}
                                          alt="Post preview"
                                          sx={{
                                            width: 60,
                                            height: 60,
                                            objectFit: 'cover',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider'
                                          }}
                                          onError={(e) => {
                                            console.error('Image load error:', e);
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      ) : (
                                        <Box
                                          sx={{
                                            width: 60,
                                            height: 60,
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            bgcolor: 'grey.50',
                                            p: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '12px',
                                            color: 'text.secondary'
                                          }}
                                        >
                                          <ArticleIcon />
                                        </Box>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2D3748' }}>
                                          {post.topic || 'Untitled Post'}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            fontSize: '0.875rem'
                                          }}
                                        >
                                          {post.content || ''}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right" sx={{ pr: 2 }}>
                                      <Stack direction="column" spacing={1} alignItems="flex-end">
                                        <Chip 
                                          label={post.type === 'carousel' ? 'Carousel' : 'Text'} 
                                          size="small"
                                          sx={{
                                            bgcolor: post.type === 'carousel' ? 'rgba(56, 161, 105, 0.1)' : 'rgba(72, 144, 254, 0.1)',
                                            color: post.type === 'carousel' ? '#38A169' : '#4890fe'
                                          }}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                          {new Date(post.created_at).toLocaleDateString('en-US', { 
                                            month: 'short',
                                            day: 'numeric'
                                          })}
                                        </Typography>
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Box sx={{ 
                            p: 4, 
                            textAlign: 'center',
                            bgcolor: 'rgba(72, 144, 254, 0.03)',
                            borderRadius: 2,
                            border: '1px dashed rgba(72, 144, 254, 0.2)'
                          }}>
                            <PostAddIcon sx={{ 
                              fontSize: 48, 
                              color: 'rgba(72, 144, 254, 0.5)',
                              mb: 2
                            }} />
                            <Typography variant="body1" sx={{ 
                              color: '#4A5568',
                              fontWeight: 500,
                              mb: 2
                            }}>
                              No draft posts yet
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => navigate('/dashboard/generate-post')}
                              sx={{
                                color: 'var(--primary-color)',
                                borderColor: 'rgba(72, 144, 254, 0.5)',
                                '&:hover': {
                                  borderColor: 'var(--primary-color)',
                                  bgcolor: 'rgba(72, 144, 254, 0.08)'
                                }
                              }}
                            >
                              Create Post
                            </Button>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Scheduled Posts */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ 
                      height: '100%',
                      background: '#ffffff',
                      border: '1px solid rgba(72, 144, 254, 0.1)',
                      boxShadow: 'none',
                      borderRadius: 2
                    }}>
                      <CardContent>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 3
                        }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2D3748' }}>
                            Scheduled Posts
                          </Typography>
                          <Button
                            size="small"
                            endIcon={<ArrowForwardIcon />}
                            onClick={() => navigate('/dashboard/calendar')}
                            sx={{
                              color: 'var(--primary-color)',
                              '&:hover': { bgcolor: 'rgba(72, 144, 254, 0.08)' }
                            }}
                          >
                            View Calendar
                          </Button>
                        </Box>
                        <Box sx={{ 
                          p: 4, 
                          textAlign: 'center',
                          bgcolor: 'rgba(72, 144, 254, 0.03)',
                          borderRadius: 2,
                          border: '1px dashed rgba(72, 144, 254, 0.2)'
                        }}>
                          <CalendarTodayIcon sx={{ 
                            fontSize: 48, 
                            color: 'rgba(72, 144, 254, 0.5)',
                            mb: 2
                          }} />
                          <Typography variant="body1" sx={{ 
                            color: '#4A5568',
                            fontWeight: 500,
                            mb: 2
                          }}>
                            No scheduled posts yet
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate('/dashboard/calendar')}
                            sx={{
                              color: 'var(--primary-color)',
                              borderColor: 'rgba(72, 144, 254, 0.5)',
                              '&:hover': {
                                borderColor: 'var(--primary-color)',
                                bgcolor: 'rgba(72, 144, 254, 0.08)'
                              }
                            }}
                          >
                            View Calendar
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
};

export default Dashboard; 